<script lang="ts" setup>
import { Routes } from '~/enums/Routes'
import { useIdentity } from '~/store/identity'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { useCartStore } from '~/store/cart'
import { useLoaderStore } from '~/store/loader'
import type { IClientInterface } from '~/types/auth/identityTypes'
import proIcon from 'assets/icons/icon-pro.svg?raw'
import { OrderFormSteps } from '~/enums/QuotePageEnums'

const props = defineProps<{
  showPro?: boolean
  isDropdown?: boolean
}>()
const emit = defineEmits(['onClose'])

const { t } = useLang()
const identity = useIdentity()
const loaderStore = useLoaderStore()
const { isAlt, isTarpininke, isWhitelabel, isNoriuLive, isShipzee } = useTheme()
const route = useRoute()
const user = ref<IClientInterface | null>(null)

user.value = await identity.getUser()

const showSitePro = computed(() => props.showPro && (isShipzee || isWhitelabel))

const sidebarLinks = computed(() => ({
  accountInformation: {
    text: t('account.account_information'),
    link: getLocalizedRoute(Routes.Account.AccountInformation),
    icon: 'fa-solid fa-gear',
    show: true,
  },
  fillOrderForm: {
    text: t('my_orders.fill_quote_form'),
    link: getLocalizedRoute(Routes.OrderForm.Index),
    icon: 'fa-solid fa-clipboard-list',
    show: isTarpininke ? true : isNoriuLive ? false : user?.value?.enable_order_form,
  },
  myOrders: {
    text: t('account.my_orders'),
    link: getLocalizedRoute(Routes.Account.MyOrders),
    icon: 'fa-solid fa-box',
    show: true,
  },
  deliveryAddresses: {
    text: t('account.delivery_addresses'),
    link: getLocalizedRoute(Routes.Account.DeliveryAddresses),
    icon: 'fa-solid fa-house',
    show: true,
  },
}))

const proLink = computed(() => ({
  text: t('account.pro_us_address'),
  link: getLocalizedRoute(Routes.Account.SitePro),
  svg: proIcon,
}))

const logoutLink = computed(() => ({
  text: t('account.log_out'),
  icon: 'fa-solid fa-right-from-bracket',
}))

async function handleLogout() {
  try {
    emit('onClose')
    loaderStore.setLoader(true)
    await identity.logout()
    await useCartStore().fetch()
  } catch (error) {
  } finally {
    loaderStore.setLoader(false)
  }
}

const onLinkClick = (link?: string) => {
  emit('onClose')

  if (link === t('my_orders.fill_quote_form')) {
    return (useLocalStorage<OrderFormSteps>('order-form-step', OrderFormSteps.ProductsStep).value =
      OrderFormSteps.ProductsStep)
  }

  return false
}
</script>

<template>
  <div
    class="menu-wrapper"
    :class="{
      'account-dropdown': isDropdown,
    }">
    <div v-if="isDropdown" class="link-group acc-dropdown">
      <div>
        <div v-if="user?.first_name || user?.last_name" class="font-medium text-lg text-text1 dark:text-dmText1">
          {{ user?.first_name }} {{ user?.last_name }}
        </div>
        <div v-if="user?.email" class="text-sm text-gray0 dark:text-dmText3 mt-1">{{ user?.email }}</div>
      </div>
      <div v-if="isAlt">
        <NuxtLink :to="getLocalizedRoute(Routes.ShipmentTracking.Tracking)">{{
          t('shipment-tracking.shipment-tracking-title')
        }}</NuxtLink>
      </div>
      <hr v-if="!isAlt" class="mt-4 mb-0" />
    </div>
    <div class="link-group">
      <template v-for="(link, index) in sidebarLinks">
        <NuxtLink v-if="link.show" :key="index" :to="link.link" @click="() => onLinkClick(link)">
          <div
            class="menu-item flex content-center items-center"
            :class="{
              'orange-filter': route?.name?.includes(link.link.name),
            }">
            <i :class="link.icon" class="text-xl fa-fw"></i>
            <span class="leading-6 pl-3">{{ link.text }}</span>
          </div>
        </NuxtLink>
      </template>
    </div>

    <template v-if="showSitePro">
      <hr v-if="isShipzee" class="my-1" />
      <div class="link-group">
        <div
          :class="{
            'pb-[0.625rem]': props.isDropdown,
          }">
          <NuxtLink :to="proLink.link" class="!pb-0" @click="() => onLinkClick(proLink.text)">
            <div
              class="menu-item flex content-center"
              :class="{
                'orange-filter': route?.name?.includes(proLink.link.name),
              }">
              <i class="w-[25px] flex items-center" v-html="proLink.svg" />
              <span class="leading-6 pl-3">{{ proLink.text }}</span>
            </div>
          </NuxtLink>
          <div v-if="isDropdown && user?.unpaid_frs_count" class="pl-8 text-sm text-gray dark:text-dmText2">
            <span v-text="t('site_pro.status.fr_status.awaiting_payment')" /><span
              class="ml-1 text-red"
              v-text="`(${user?.unpaid_frs_count})`" />
          </div>
        </div>
      </div>
    </template>
    <hr v-if="!isAlt" class="my-1" />
    <button class="logout-link" @click.prevent="handleLogout()">
      <i :class="logoutLink.icon + ' text-xl fa-fw'"></i>
      <span class="leading-6 pl-3">{{ logoutLink.text }}</span>
    </button>
  </div>
</template>

<style lang="postcss" scoped>
.menu-wrapper {
  @apply flex flex-col;
  gap: 0.625rem;
  color: theme('colors.black');

  a,
  .logout-link {
    @apply flex items-center text-text1 dark:text-white;
    padding: 0.625rem 0;
  }

  a:hover,
  .logout-link:hover,
  .orange-filter {
    color: theme('colors.brand.primary');
    text-decoration: none;
    cursor: pointer;
  }

  .menu-item {
    :deep(svg) {
      path {
        @apply w-[25px] h-[21px] dark:fill-white;
      }
    }

    &:hover,
    &.orange-filter {
      :deep(svg) {
        path {
          @apply fill-brand-primary dark:fill-brand-primary;
        }
      }
    }
  }
}

.alt {
  .account-dropdown {
    .link-group {
      @apply bg-white dark:bg-dmGray1;

      > * {
        @apply p-4 border-b border-neutral5 first-of-type:border-t;

        > a {
          @apply p-0;
        }
      }
    }

    .logout-link {
      @apply bg-white dark:bg-dmGray1 p-4 border-y border-neutral5;
    }
  }
}

.wl {
  .account-dropdown {
    .link-group.acc-dropdown {
      > div {
        @apply border-t-0;
      }
    }
  }
}
</style>
